import React from "react";
import { Box, Typography, Container, Grid, Button, useTheme, useMediaQuery } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import { Link } from "react-router-dom";
import villas from "../../../constants/villas.json";

const Properties = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const properties = villas.slice(0, 3);

  return (
    <Box
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
      }}
    >
      <Container maxWidth="lg">
        {/* Header Section with Category, Title and Description */}
        <Box sx={{ mb: 6 }}>
          <Grid container spacing={1}>
            <Grid item size={{ xs: 12, md: 6 }}>
              {/* Category Label */}
              <Typography
                data-aos="fade-up"
                sx={{
                  color: theme.palette.text.secondary,
                  fontFamily: "Monument-Private",
                  fontWeight: 500,
                  letterSpaxcing: 2,
                  fontSize: "0.75rem",
                  mb: 2,
                }}
              >
                STAYS
              </Typography>

              {/* Main Heading */}
              <Typography
                variant="h2"
                component="h2"
                data-aos="fade-up"
                data-aos-delay="200"
                sx={{
                  fontFamily: "Monument-Private",
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                  lineHeight: 1.1,
                  fontWeight: "bold",
                  color: theme.palette.text.primary,
                }}
              >
                Properties for rent in Goa
              </Typography>
            </Grid>

            {/* Description on the right */}
            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography
                variant="body1"
                data-aos="fade-up"
                data-aos-delay="400"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "1rem",
                  lineHeight: 1.6,
                  mt: { xs: 0, md: 4 }, // Add top margin on desktop to align with heading
                  fontWeight: 400,
                }}
              >
                Discover our handpicked selection of beautiful villas and apartments in Goa. Each property is curated for comfort, style, and a memorable stay—perfect for families, friends, or solo travelers seeking a unique Goan experience.
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Property Cards Grid */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {properties.map((property, index) => (
            <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={property.id}>
              <Link to={`/property/${property.id}`} style={{ textDecoration: "none" }}>
                <Box
                  data-aos="fade-up"
                  data-aos-delay={index * 200}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  {/* Rounded Image */}
                  <Box
                    component="img"
                    src={property.image}
                    alt={property.name}
                    sx={{
                      width: "100%",
                      height: 240,
                      objectFit: "cover",
                      borderRadius: 4,
                      mb: 2,
                    }}
                  />

                  {/* Content */}
                  <Box sx={{ flexGrow: 1 }}>
                    {/* Villa Title */}
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        color: theme.palette.text.primary,
                        fontFamily: "Monument-Private",
                      }}
                    >
                      {property.name}
                    </Typography>

                    {/* Villa Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        lineHeight: 1.6,
                        fontSize: "0.9rem",
                        fontWeight: 400,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minHeight: '2.8em', // ensures consistent card height for 2 lines
                      }}
                    >
                      {property.description}
                    </Typography>
                  </Box>
                </Box>
              </Link>
            </Grid>
          ))}
        </Grid>

        {/* See All Villas Button */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Link to="/properties" style={{ textDecoration: "none" }}>
            <Button
              variant="outlined"
              data-aos="fade-up"
              data-aos-delay="600"
              endIcon={<ArrowForward sx={{ fontSize: "1rem" }} />}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                px: 4,
                py: 1.5,
                borderRadius: 6,
                borderColor: theme.palette.text.primary,
                color: theme.palette.text.primary,
                fontSize: "1rem",
                borderWidth: 1,
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: theme.palette.text.primary,
                  color: "white",
                  borderColor: theme.palette.text.primary,
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                },
              }}
            >
              See all stays
            </Button>
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

export default Properties;
